import type { Volume } from '~/shared/types/api';
import { isOrganizationView } from '~/shared/utils/accountUtils';
import { appendOrgAccountsScope } from '~/shared/utils/organizationUtils';
import type { Account } from '~/shared/utils/types';

import { buildAccountCache } from './buildAccountCache';
import { buildApiVolumeByEbsId, buildCachesFromVolumes } from './buildExplorationVolumeCaches';
import { transformApiVolume } from './transformVolume';
import type { ExplorationVolumeLoadResult, LoadingProgress } from '../types/exploration';

const PAGE_SIZE = 200;
const CONCURRENCY = 10;

const VOLUME_SORT_BY = JSON.stringify({ field: 'volumeId', ascending: true });

type FetchFn = <T>(url: string) => Promise<T>;

function volumesUrl(
  limit: number,
  offset: number,
  authAccounts: Account[],
  activeTenantId: string | undefined
) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    sortBy: VOLUME_SORT_BY,
  });
  appendOrgAccountsScope(params, authAccounts, activeTenantId);
  return `/api/v1/volumes?${params.toString()}`;
}

function volumeCountUrl(authAccounts: Account[], activeTenantId: string | undefined) {
  const params = new URLSearchParams();
  appendOrgAccountsScope(params, authAccounts, activeTenantId);
  const qs = params.toString();
  return qs ? `/api/v1/volumes/count?${qs}` : `/api/v1/volumes/count`;
}

async function fetchAllVolumes(
  api: FetchFn,
  authAccounts: Account[],
  activeTenantId: string | undefined,
  onProgress?: (progress: LoadingProgress) => void,
  signal?: AbortSignal,
  progressAccountName?: string
): Promise<Volume[]> {
  const countRes = await api<{ count?: number }>(volumeCountUrl(authAccounts, activeTenantId));
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const totalCount = countRes.count || 0;
  onProgress?.({
    loaded: 0,
    total: totalCount,
    status: 'Fetching volumes…',
    accountName: progressAccountName,
  });
  if (totalCount === 0) return [];

  const numPages = Math.ceil(totalCount / PAGE_SIZE);
  const results: Volume[][] = new Array(numPages);
  let nextPage = 0;
  let completed = 0;

  async function worker() {
    while (true) {
      if (signal?.aborted) return;
      const i = nextPage++;
      if (i >= numPages) return;
      const offset = i * PAGE_SIZE;
      const data = await api<{ volumes?: Volume[] }>(
        volumesUrl(PAGE_SIZE, offset, authAccounts, activeTenantId)
      );
      results[i] = data.volumes || [];
      completed++;
      onProgress?.({
        loaded: Math.min(completed * PAGE_SIZE, totalCount),
        total: totalCount,
        status: `${Math.min(completed * PAGE_SIZE, totalCount).toLocaleString()} / ${totalCount.toLocaleString()} volumes`,
        accountName: progressAccountName,
      });
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, numPages) }, worker));
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  return results.flat();
}

export async function fetchExplorationVolumeCaches(
  api: FetchFn,
  authAccounts: Account[],
  activeTenantId: string | undefined,
  onProgress?: (progress: LoadingProgress) => void,
  signal?: AbortSignal
): Promise<ExplorationVolumeLoadResult> {
  const acct =
    authAccounts.find((a) => a.accountId === activeTenantId) ?? authAccounts[0];
  const orgView = isOrganizationView(activeTenantId, authAccounts);
  const progressName = orgView
    ? acct?.accountName || 'Organization'
    : acct?.accountName || acct?.accountId || '';

  const rawVols = await fetchAllVolumes(
    api,
    authAccounts,
    activeTenantId,
    onProgress,
    signal,
    progressName
  );

  if (orgView) {
    return buildCachesFromVolumes(rawVols, authAccounts, activeTenantId);
  }

  if (!acct?.accountId) {
    return { accounts: {}, apiVolumeByEbsId: {} };
  }

  const volumes = rawVols.map((v) => {
    const vol = transformApiVolume(v);
    vol.accountId = vol.accountId || acct.accountId;
    vol.accountName = acct.accountName || acct.accountId;
    return vol;
  });

  return {
    accounts: {
      [acct.accountId]: buildAccountCache(acct.accountId, acct.accountName || acct.accountId, volumes),
    },
    apiVolumeByEbsId: buildApiVolumeByEbsId(rawVols),
  };
}
